// Pictures and PDFs the customer sends: picked (camera, photos, files),
// pasted or dropped. Before upload a photo is shrunk and re-encoded as JPEG,
// which keeps phone photos small and drops their EXIF data (GPS position
// included). A PDF goes as it is.

/** The server's limit (chat/pictures.ts). */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_SIDE = 1600;

export function isPdf(f: File): boolean {
  return f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
}

/** What the chat takes: pictures and PDFs. */
export function isSendableFile(f: File): boolean {
  return f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name) || isPdf(f);
}

/** The pictures/PDFs on the clipboard, or null to let a text paste happen (Word/Excel also put a picture of the text there). */
export function pastedFiles(e: ClipboardEvent): File[] | null {
  const dt = e.clipboardData;
  if (!dt) return null;
  const files = Array.from(dt.files ?? []).filter(isSendableFile);
  if (!files.length) return null;
  const text = dt.getData('text/plain').trim();
  if (text && !text.split(/[\r\n]+/).some((line) => files.some((f) => f.name === line.trim()))) return null;
  // A screenshot on the clipboard is just "image.png".
  return files.map((f) => (/^image\.\w+$/i.test(f.name) ? new File([f], f.name.replace(/^image/i, 'skjermbilde'), { type: f.type }) : f));
}

/**
 * Ready to upload: a photo shrunk and re-encoded; a small PNG (screenshot), a
 * GIF or a PDF as it is. Undecodable (HEIC in Chrome): sent as is.
 */
export async function prepareFile(file: File): Promise<{ blob: Blob; name: string }> {
  if (isPdf(file)) {
    return { blob: file.type === 'application/pdf' ? file : new Blob([file], { type: 'application/pdf' }), name: file.name || 'dokument.pdf' };
  }
  const base = (file.name || 'bilde').replace(/\.[A-Za-z0-9]{1,5}$/, '') || 'bilde';
  if (file.type === 'image/gif') return { blob: file, name: file.name || 'bilde.gif' };
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
    if (file.type === 'image/png' && scale === 1 && file.size <= 1.5 * 1024 * 1024) {
      bmp.close();
      return { blob: file, name: file.name || 'bilde.png' };
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    const g = canvas.getContext('2d');
    if (!g) throw new Error('no canvas');
    g.fillStyle = '#fff'; // transparent screenshots: white, not black
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close();
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
    if (blob) return { blob, name: `${base}.jpg` };
  } catch {
    /* not decodable here — the server checks what it is */
  }
  return { blob: file, name: file.name || 'bilde' };
}
